import { apiClient } from '@/hooks/useApi';

export interface WorkOrderSummaryItem {
  workOrderId: number;
  workOrderNumber: string;
  createdDate: string;
  completedDate: string | null;
  status: string;
  customerName: string;
  vehicle: string;
  totalAmount: number;
}

export interface NotificationPreview {
  notificationId: number;
  customerName: string;
  workOrderNumber: string;
  message: string;
  phoneNumber: string;
  createdAt: string;
}

export interface InventoryAlert {
  partId: number;
  partName: string;
  quantityOnHand: number;
  minimumStockLevel: number;
}

export interface DashboardSummary {
  todaysCheckins: number;
  yesterdayCheckins: number;
  lastSevenDaysCount: number;
  pendingCheckins: number;
  openNotifications: number;
  activeCustomers: number;
  activeWorkOrders: number;
  todayCompletedCount: number;
  pendingApprovals: number;
  outstandingInvoices: number;
  outstandingAmount: number;
  totalActiveTechs: number;
  techsWithActiveWork: number;
  techsAvailableCount: number;
  baysOccupied: number;
  totalBays: number;
  partsOnOrder: number;
  jobBoard: WorkOrderSummaryItem[];
  notifications: NotificationPreview[];
  inventoryAlerts: InventoryAlert[];
}

export interface TechnicianAssignedJob {
  workOrderId: number;
  workOrderNumber: string;
  vehicle: string;
  status: string;
  createdDate: string;
}

export interface TechnicianDetail {
  staffId: number;
  fullName: string;
  firstName: string;
  lastName: string;
  jobLimit: number;
  currentJobCount: number;
  capacityPercentage: number;
  capacityStatus: 'Normal' | 'AtLimit' | 'OverLimit';
  assignedJobs: TechnicianAssignedJob[];
}

export interface TechnicianWorkloadResponse {
  technicians: TechnicianDetail[];
}

export interface BayJob {
  workOrderId: number;
  workOrderNumber: string;
  vehicle: string;
  customerName: string;
  assignedTechName: string;
  serviceDescription: string;
  status: string;
  createdDate: string;
  timeInBay?: string;
}

export interface BayDetail {
  bayId: number;
  bayNumber: number;
  status: 'Free' | 'Occupied' | 'Transitioning';
  currentJob?: BayJob;
}

export interface BayUtilizationResponse {
  totalBays: number;
  occupiedBays: number;
  bays: BayDetail[];
}

export interface PartJobUsage {
  workOrderId: number;
  workOrderNumber: string;
  vehicle: string;
  quantity: number;
}

export interface PartInUse {
  partId: number;
  partName: string;
  totalQuantityInUse: number;
  jobs: PartJobUsage[];
}

export interface PartsUtilizationResponse {
  totalPartsInUse: number;
  activeJobs: number;
  parts: PartInUse[];
}

export const dashboardApi = {
  async getSummary(): Promise<DashboardSummary> {
    const { data } = await apiClient.get<DashboardSummary>('/api/dashboard/summary');
    return data;
  },

  async getTechnicianWorkload(): Promise<TechnicianWorkloadResponse> {
    const { data } = await apiClient.get<TechnicianWorkloadResponse>('/api/dashboard/technicians/detailed');
    return data;
  },

  async getBayUtilization(): Promise<BayUtilizationResponse> {
    const { data } = await apiClient.get<BayUtilizationResponse>('/api/dashboard/bays/detailed');
    return data;
  },

  async getPartsInUse(): Promise<PartsUtilizationResponse> {
    const { data } = await apiClient.get<PartsUtilizationResponse>('/api/dashboard/parts/in-use');
    return data;
  },
};
